// ============================================================
// ingestionPipeline.js â€” Chunk and index documents at upload time
// Crestview Eighty HOA Portal
//
// Called after saveDocument() succeeds.
// Writes chunks to document_chunks (GIN-indexed for FTS).
// Does NOT set ai_status â€” that is Level 4 admin approval only.
// Triggers embed-chunks Edge Function after indexing (non-blocking).
// ============================================================

import { chunkDocument } from './chunkUtils';

const BATCH_SIZE = 50; // Insert chunks in batches to stay under Supabase payload limits

/**
 * Index a document into document_chunks.
 * Idempotent: deletes existing chunks before re-inserting.
 *
 * @param {object} supabase  - Supabase client
 * @param {string} documentId
 * @param {string} hoaId
 * @returns {Promise<{ chunksWritten: number, error: string|null }>}
 */
export async function runIngestionPipeline(supabase, documentId, hoaId) {
  try {
    // 1. Fetch the document row
    const { data: doc, error: fetchError } = await supabase
      .from('documents')
      .select('id, title, category, extracted_text, phase_applicability, ai_status')
      .eq('id', documentId)
      .eq('hoa_id', hoaId)
      .maybeSingle();

    if (fetchError) throw new Error(`Fetch failed: ${fetchError.message}`);
    if (!doc) throw new Error(`Document not found: ${documentId}`);
    if (!doc.extracted_text || doc.extracted_text.trim().length < 50) {
      console.warn(`[ingestionPipeline] Skipping ${documentId} â€” insufficient text`);
      return { chunksWritten: 0, error: 'insufficient_text' };
    }

    // 2. Delete existing chunks (idempotent re-index)
    const { error: deleteError } = await supabase
      .from('document_chunks')
      .delete()
      .eq('document_id', documentId)
      .eq('hoa_id', hoaId);

    if (deleteError) throw new Error(`Chunk delete failed: ${deleteError.message}`);

    // 3. Chunk the document
    const chunks = chunkDocument(doc);
    if (chunks.length === 0) {
      console.warn(`[ingestionPipeline] No chunks produced for ${documentId}`);
      return { chunksWritten: 0, error: 'no_chunks' };
    }

    // 4. Build insert rows
    const rows = chunks.map((chunk, i) => ({
      hoa_id: hoaId,
      document_id: documentId,
      chunk_text: chunk.text,
      chunk_index: i,
      char_offset_start: chunk.charOffsetStart || 0,
      char_offset_end: chunk.charOffsetEnd || 0,
      document_class: doc.category,
      section_header: chunk.sectionHeader || null,
      is_definition: Boolean(chunk.isDefinition),
      amends_reference: null,
      effective_date: null,
      document_period_date: null,
      embedding: null,       // Deferred â€” pgvector hybrid search at Tenant 2
      invalidated_at: null,
    }));

    // 5. Batch insert
    let totalInserted = 0;
    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
      const batch = rows.slice(i, i + BATCH_SIZE);
      const { error: insertError } = await supabase
        .from('document_chunks')
        .insert(batch);

      if (insertError) throw new Error(`Chunk insert failed (batch ${Math.floor(i / BATCH_SIZE) + 1}): ${insertError.message}`);
      totalInserted += batch.length;
    }

    // 6. Mark document as indexed + update chunk_count (does NOT touch ai_status)
    const { error: updateError } = await supabase
      .from('documents')
      .update({
        is_indexed: true,
        chunk_count: totalInserted,
        indexed_at: new Date().toISOString(),
        last_chunked_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', documentId);

    if (updateError) {
      console.error(`[ingestionPipeline] is_indexed update failed for ${documentId}:`, updateError.message);
      // Non-fatal â€” chunks are written, just flag is wrong
    }

    // 7. Generate embeddings for new chunks (non-blocking)
    // Embedding failure never fails the upload â€” chunks are indexed, embeddings are best-effort
    try {
      const { error: embedError } = await supabase.functions.invoke('embed-chunks', {
        body: { hoa_id: hoaId },
      });
      if (embedError) {
        console.warn(`[ingestionPipeline] embed-chunks failed for ${documentId}:`, embedError.message);
      } else {
        console.warn(`[ingestionPipeline] Embeddings generated for document ${documentId}`);
      }
    } catch (embedErr) {
      console.warn(`[ingestionPipeline] embed-chunks threw for ${documentId}:`, embedErr.message);
    }

    console.warn(`[ingestionPipeline] Indexed ${totalInserted} chunks for document ${documentId}`);
    return { chunksWritten: totalInserted, error: null };

  } catch (err) {
    console.error(`[ingestionPipeline] Fatal error for document ${documentId}:`, err.message);
    return { chunksWritten: 0, error: err.message };
  }
}

/**
 * Re-index all unindexed documents for an HOA.
 * Used for backfilling existing documents.
 *
 * @param {object} supabase
 * @param {string} hoaId
 * @returns {Promise<{ processed: number, failed: number }>}
 */
export async function backfillIngestion(supabase, hoaId) {
  const { data: docs, error } = await supabase
    .from('documents')
    .select('id')
    .eq('hoa_id', hoaId)
    .eq('is_indexed', false)
    .not('extracted_text', 'is', null);

  if (error) {
    console.error('[backfillIngestion] Fetch failed:', error.message);
    return { processed: 0, failed: 0 };
  }

  let processed = 0, failed = 0;
  for (const doc of (docs || [])) {
    const result = await runIngestionPipeline(supabase, doc.id, hoaId);
    if (result.error && result.error !== 'insufficient_text') failed++;
    else processed++;
    // Small delay to avoid hammering the DB
    await new Promise(r => setTimeout(r, 100));
  }

  console.warn(`[backfillIngestion] Done. Processed: ${processed}, Failed: ${failed}`);
  return { processed, failed };
}
